import { control } from '../fields'
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { CalendarDays, Clock, ExternalLink, Users } from 'lucide-react'
import { fetchContent, has, text } from '../api/content'
import StudioImage from './StudioImage'
import {
  availabilityTone,
  describeQuery,
  fetchCatalog,
  registerHref,
  timeRange,
  type CatalogClass,
  type CatalogClassType,
  type CatalogQuery,
} from '../api/catalog'

const TONE: Record<'full' | 'low' | 'open', string> = {
  full: 'bg-surface-2 text-ink-3',
  low: 'bg-warn-bg text-warn',
  open: 'bg-good-bg text-good',
}

/**
 * The public class list — the only page reachable without an account (D-28).
 *
 * Written for a parent deciding whether to enrol, not for staff. No jargon, no
 * ids, no room numbers; the questions it answers are "what could my child do",
 * "when does it run", and "is there space".
 *
 * Grouped by weekday because that is how the decision is actually made — a
 * family knows which evenings they can drive, and works out from there.
 */
export default function CatalogView() {
  const [query, setQuery] = useState<CatalogQuery>({})

  const { data, isPending, isError } = useQuery({
    queryKey: ['catalog', query],
    queryFn: () => fetchCatalog(query),
    placeholderData: (previous) => previous,
  })

  const { data: content } = useQuery({ queryKey: ['content'], queryFn: fetchContent })
  const registerUrl = text(content, 'register.url')

  const nothingMatches = (data?.types.length ?? 0) === 0 && (data?.ungrouped.length ?? 0) === 0
  const set = (patch: Partial<CatalogQuery>) => setQuery((q) => ({ ...q, ...patch }))
  return (
    <div className="mx-auto max-w-[56rem] px-5 py-10">
      <header className="mb-6">
        <h1 className="font-display text-3xl">Classes</h1>

        {data?.seasonName && (
          <p className="mt-1.5 text-[14px] text-ink-2">
            {data.seasonName}
            {data.registrationOpen ? (
              <>
                {' '}
                &middot; registration is open
                {data.registrationClosesOn && (
                  <> until {new Date(`${data.registrationClosesOn}T00:00:00`).toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}</>
                )}
              </>
            ) : (
              <> &middot; registration is closed for now</>
            )}
          </p>
        )}

        {/*
          Said once, at the top, rather than on every card. A parent who is
          about to be sent to a different-looking site should know before they
          click, not be surprised by it (D-111).
        */}
        {data?.registrationOpen && registerUrl && has(content, 'register.note') && (
          <p className="mt-3 text-[13px] text-ink-3">{text(content, 'register.note')}</p>
        )}

        {data && !data.registrationOpen && (
          // Said plainly rather than left to be inferred from missing buttons.
          <p className="mt-3 rounded-xl border border-line bg-surface px-4 py-3 text-[13.5px] text-ink-2">
            You can still browse what we offer. Get in touch and we will let you know
            when places open up.
          </p>
        )}
      </header>

      <div className="mb-6 flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1">
          <span className="text-[12px] font-semibold text-ink-3">Dancer&rsquo;s age</span>
          <input
            type="number"
            min={1}
            max={99}
            placeholder="Any"
            value={query.age ?? ''}
            onChange={(e) =>
              set({ age: e.target.value === '' ? undefined : Number(e.target.value) })
            }
            className={`${control} w-24`}
          />
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-[12px] font-semibold text-ink-3">Day</span>
          <select
            value={query.day ?? ''}
            onChange={(e) => set({ day: e.target.value || undefined })}
            className={control}
          >
            <option value="">Any day</option>
            {data?.days.map((day) => (
              <option key={day} value={day}>
                {day}
              </option>
            ))}
          </select>
        </label>

        {/*
          There is deliberately no Style filter here (D-205).

          It offered a dropdown that could only ever match nothing: `Style` is
          populated on 0 of the studio's 185 classes, nothing writes it, and a
          family who picked a value would be shown an empty page. The field
          survives on the class; the filter does not, because a filter that
          cannot match is worse than no filter at all.
        */}

        <label className="flex items-center gap-2 py-2.5 text-[13.5px]">
          <input
            type="checkbox"
            checked={query.hideFull ?? false}
            onChange={(e) => set({ hideFull: e.target.checked })}
            className="size-4 accent-[var(--color-accent)]"
          />
          Only classes with space
        </label>

        {(query.age !== undefined || query.day || query.style || query.hideFull) && (
          <button
            type="button"
            onClick={() => setQuery({})}
            className="py-2.5 text-[13px] font-semibold text-accent-2 hover:underline"
          >
            Clear
          </button>
        )}
      </div>

      {isPending && <p className="text-sm text-ink-3">Loading classes...</p>}

      {isError && (
        <p className="rounded-xl border border-crit bg-crit-bg px-4 py-3 text-sm text-crit">
          We could not load the class list just now. Please try again shortly.
        </p>
      )}

      {data && nothingMatches && (
        <div className="rounded-2xl border border-line bg-surface px-5 py-8 text-center">
          <p className="text-[15px] font-semibold">Nothing matches that</p>
          <p className="mx-auto mt-1.5 max-w-[28rem] text-[13.5px] text-ink-3">
            {describeQuery(query)
              ? `We have no classes for ${describeQuery(query)} right now. Try widening the search, or get in touch and we will help you find a fit.`
              : 'Our class list for the season is not published yet. Please check back soon.'}
          </p>
        </div>
      )}

      {/*
        Grouped by what the studio calls it, not by weekday (D-191).

        A family choosing a first dance class is asking "what could she do",
        and the old page answered "here are 23 Jackrabbit strings, by night".
        The day filter above still exists for the family who already knows —
        it just is not the shape of the page any more.
      */}
      <div className="flex flex-col gap-10">
        {data?.types.map((type) => (
          <ClassTypeSection key={type.classTypeId} type={type} registerUrl={registerUrl} />
        ))}

        {/*
          Classes nobody has filed, listed plainly. Dropping them would hide a
          class from the families it is for, which is the one failure a catalog
          cannot afford — so this section is deliberately unremarkable rather
          than absent.
        */}
        {(data?.ungrouped.length ?? 0) > 0 && (
          <section>
            <h2 className="mb-2.5 flex items-center gap-2 text-[12px] font-bold tracking-[0.12em] text-ink-3 uppercase">
              <CalendarDays size={13} strokeWidth={2.5} />
              More classes
            </h2>

            <ul className="flex flex-col gap-2.5">
              {data?.ungrouped.map((item) => (
                <ClassCard key={item.classId} item={item} registerUrl={registerUrl} />
              ))}
            </ul>
          </section>
        )}
      </div>
    </div>
  )
}

/**
 * One kind of class — a photograph, what it is, then when it runs.
 *
 * <b>The order is the argument.</b> A parent decides on the programme and only
 * then on a time, so the times sit underneath rather than leading. The
 * photograph does the work no sentence can: it shows the age of the children
 * and the room they are in.
 */
function ClassTypeSection({
  type,
  registerUrl,
}: {
  type: CatalogClassType
  registerUrl: string
}) {
  return (
    <section>
      {/*
        An accent rail down the side of each programme (D-201). The public page
        is marketing and can carry more of the studio's pink than an admin
        screen — this reads as brand at a glance without putting colour on
        anything a parent has to read.
      */}
      <div className="overflow-hidden rounded-2xl border border-line border-l-[3px] border-l-accent bg-surface">
        {/*
          Rendered only when a photograph has been chosen. A placeholder box
          would look like a picture that failed to load, which reads worse on a
          marketing page than a clean heading with no image at all.

          Framed as the studio positioned it (D-199) — resolved server-side, so
          this page and the admin preview can never disagree about which
          framing wins.
        */}
        {type.heroUrl && (
          <StudioImage
            src={type.heroUrl}
            alt=""
            framing={type.heroFraming}
            className="h-48 w-full sm:h-60"
          />
        )}

        <div className="px-5 py-4">
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <h2 className="font-display text-2xl">{type.name}</h2>
            {type.ageLabel && (
              <span className="rounded-md bg-accent-soft px-2 py-0.5 text-[12.5px] font-semibold text-accent-2">
                {type.ageLabel}
              </span>
            )}
          </div>

          {type.blurb && (
            <p className="mt-2 max-w-[44rem] text-[14px] leading-relaxed text-ink-2">
              {type.blurb}
            </p>
          )}

          {/*
            Said once, not once per class (D-212). Five Happy Feet sections
            carry the same paragraph in the real export, and printed on every
            card it reads as five different things to compare rather than one
            thing said five times. A class that describes itself differently
            still does so, on its own card.
          */}
          {type.sharedDescription && (
            <p className="mt-2 max-w-[44rem] text-[13.5px] leading-relaxed text-ink-3">
              {type.sharedDescription}
            </p>
          )}

          <p className="mt-3 text-[12px] font-bold tracking-[0.12em] text-ink-3 uppercase">
            {type.classes.length} {type.classes.length === 1 ? 'class' : 'classes'} to choose from
          </p>

          <ul className="mt-2.5 flex flex-col gap-2.5">
            {type.classes.map((item) => (
              <ClassCard key={item.classId} item={item} registerUrl={registerUrl} />
            ))}
          </ul>
        </div>
      </div>
    </section>
  )
}

function ClassCard({ item, registerUrl }: { item: CatalogClass; registerUrl: string }) {
  const href = registerHref(registerUrl, item.registrationId)

  return (
    <li className="rounded-2xl border border-line bg-surface px-4 py-3.5">
      <div className="flex flex-wrap items-start gap-3">
        <div className="min-w-0 flex-1">
          {/* Machine-shaped names from Jackrabbit; see CalendarWeekView. */}
          <h3 className="text-[15px] font-semibold wrap-anywhere">{item.name}</h3>

          <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px] text-ink-2">
            <span className="flex items-center gap-1.5">
              <Clock size={13} strokeWidth={2} className="shrink-0 text-ink-3" />
              {timeRange(item.startTime, item.endTime)}
            </span>
            <span>{item.ageLabel}</span>
            {item.style && <span>{item.style}</span>}
          </p>

          {item.description && (
            <p className="mt-1.5 text-[13px] text-ink-3">{item.description}</p>
          )}

          {item.teachers.length > 0 && (
            <p className="mt-1.5 flex items-center gap-1.5 text-[12.5px] text-ink-3">
              <Users size={12} strokeWidth={2} className="shrink-0" />
              {item.teachers.join(', ')}
            </p>
          )}
        </div>

        <div className="flex shrink-0 flex-col items-end gap-2">
          <span
            className={`rounded-md px-2 py-0.5 text-[12px] font-semibold ${TONE[availabilityTone(item)]}`}
          >
            {item.availability}
          </span>

          {item.canRegister && href ? (
            /*
             * Out to Jackrabbit, which takes the details and the payment
             * method (D-111), with this class already selected where we know
             * its id. `noreferrer` as well as `noopener` because this leaves
             * our site for one that holds families' card details — the referrer
             * tells them nothing they need.
             */
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 rounded-lg bg-accent px-3.5 py-2 text-[13px] font-semibold text-on-accent no-underline hover:brightness-110"
            >
              Register
              <ExternalLink size={12} strokeWidth={2.5} />
            </a>
          ) : (
            /*
              Replaced in place rather than hidden. A parent looking for a class
              needs to find it and learn it is full — not conclude the studio
              stopped running it.
            */
            <span className="px-1 text-[12px] text-ink-3">
              {item.isFull ? 'No spaces left' : 'Ask us about this one'}
            </span>
          )}
        </div>
      </div>
    </li>
  )
}
